import type {Schemas} from 'domainTypes/dto';
import type {Concept, EntityBase} from 'types';
import type {HttpError} from 'api/http/types';
import type {
  ChangeRequestAction,
  CodeListMap,
  Data,
  LinkedAgentMultiRole,
  LinkedLiterary,
  Schema,
  SchemaModifier,
} from 'schemaDefinition/types';
import type {MetadataModifierMap} from 'schemas/types';
import type {WorkPrizeWon} from 'services/prizes/types';
import type {TaskStatus} from 'services/tasks/types';
import type {Thesaurus, ThesaurusId} from 'services/thesaurus/types';
import type {RootState} from 'store/types';

export type GetDataBasedModifier = (data: Data) => SchemaModifier | undefined;
type MetadataSchema = {
  schema: Schema;
  modifiers: MetadataModifierMap;
};

export type MetadataSchemas = {
  work: MetadataSchema;
  expression: MetadataSchema;
  manifestation: MetadataSchema;
};

export type MetadataEditConfiguration = {
  schemas: MetadataSchemas | undefined;
  codelists: CodeListMap;
};

export type ThesaurusMap = {[key in ThesaurusId]?: Thesaurus};

//----- Task ---------------------------------------------------------------------------------------

export type ManifestationStatus =
  Required<Schemas.ManifestationBaseDto>['status'];

export type ExternalSuggestions = Schemas.ExternalSuggestionsDto;
export type ManifestationExternalSuggestions =
  Required<Schemas.ManifestationExternalSuggestionsDto>;

//----- Work ------------------------------------------------------------------------------

export type Dewey = Schemas.DeweyDto;

export type WorkV4 = EntityBase & {
  type: Required<Schemas.WorkBaseDto>['type'];
  firstPublishedYear?: Required<Schemas.WorkBaseDto>['firstPublishedYear'];
  preferredTitle?: Schemas.WorkTitleInfoDto['preferredTitle'];
  originalTitle?: Schemas.WorkTitleInfoDto['originalTitle'];
  otherTitles?: OtherTitle[];
  languages?: Schemas.WorkBaseDto['languages'];
  dewey?: Schemas.WorkBaseDto['dewey'];
  externalDewey?: Schemas.WorkBaseDto['externalDewey'];
  intellectualLevel?: Schemas.WorkBaseDto['intellectualLevel'];
  literatureType?: Schemas.WorkBaseDto['literatureType'];
  themes?: Schemas.WorkBaseDto['themes'];
  genreAndForm?: Schemas.WorkBaseDto['genreAndForm'];
  subjects?: Schemas.WorkBaseDto['subjects'];
  norwegianSubjects?: Schemas.WorkBaseDto['norwegianSubjects'];

  bibleVersion?: Schemas.WorkBaseDto['bibleVersion'];
  bibleContent?: Schemas.WorkBaseDto['bibleContent'];

  agents?: LinkedAgentMultiRole[];
  links?: LinkedLiterary[];
  prizes?: WorkPrizeWon[];

  educationLevel?: Schemas.WorkBaseDto['educationLevel'];
  educationTargetGroup?: Schemas.WorkBaseDto['educationTargetGroup'];
  grep?: string[];
  generalNote?: string[];
  norwegianForAdultImmigrants?: Schemas.WorkBaseDto['norwegianForAdultImmigrants'];
  useTitleAsMainEntry?: Schemas.WorkBaseDto['useTitleAsMainEntry'];
  teachingMaterials?: Schemas.WorkBaseDto['teachingMaterials'];
  noteForCataloging?: Schemas.WorkBaseDto['noteForCataloging'];

  bookGroup?: Schemas.WorkBaseDto['bookGroup'];
};

//----- Expression ------------------------------------------------------------------------------

export type ExpressionV4 = EntityBase & {
  workId: Required<Schemas.ExpressionBaseDto>['workId'];
  expressionFormat?: Schemas.ExpressionBaseDto['expressionFormat'];
  languages?: Schemas.ExpressionBaseDto['languages'];
  intermediateOriginalLanguage?: Schemas.ExpressionBaseDto['intermediateOriginalLanguage'];
  content?: Schemas.ExpressionBaseDto['content'];
  agents?: LinkedAgentMultiRole[];
  // firstPublishedYear is a readonly value.
  // This value is firstPublishedYearOverride or the lowest publishedYear on manifestations.
  // EBS calculates this for frontend so our expression previews can be
  // separated from manifestations.
  firstPublishedYear?: Schemas.ExpressionBaseDto['firstPublishedYear'];
  firstPublishedYearOverride?: Schemas.ExpressionBaseDto['firstPublishedYearOverride'];
};

//----- Manifestation ------------------------------------------------------------------------------

export type ManifestationV4 = EntityBase &
  PhysicalProperties &
  AgentsDescription & {
    expressionId: Required<Schemas.ManifestationBaseDto>['expressionId'];
    status: Required<Schemas.ManifestationBaseDto>['status'];
    isbn?: Schemas.ManifestationBaseDto['isbn'];
    ismn?: Schemas.ManifestationBaseDto['ismn'];
    ean?: Schemas.ManifestationBaseDto['ean'];
    basedOnIsbn?: Schemas.ManifestationBaseDto['basedOnIsbn'];
    digitalVersionIsbn?: Schemas.ManifestationBaseDto['digitalVersionIsbn'];
    wrongIsbn?: Schemas.ManifestationBaseDto['wrongIsbn'];
    mainTitles?: MainTitle[];
    otherTitles?: OtherTitle[];
    edition?: Schemas.ManifestationBaseDto['edition'];
    publishedYear?: Schemas.ManifestationBaseDto['publishedYear'];
    pageCount?: Schemas.ManifestationBaseDto['pageCount'];
    contentReceivedDate?: Schemas.ManifestationBaseDto['contentReceivedDate'];
    externalSystemInfo?: Schemas.ManifestationBaseDto['externalSystemInfo'];

    productForm?: Schemas.ManifestationBaseDto['productForm'];
    productFormDetail?: Schemas.ManifestationBaseDto['productFormDetail'];
    productGroup?: Schemas.ManifestationBaseDto['productGroup'];
    bookGroup?: Schemas.ManifestationBaseDto['bookGroup'];
    fileFormatVersion?: Schemas.ManifestationBaseDto['fileFormatVersion'];

    productOwners?: Schemas.ManifestationBaseDto['productOwners'];
    products?: Product[];
    ddsStatus?: DDSStatus[];

    packQuantity?: Schemas.ManifestationBaseDto['packQuantity'];

    imprints?: LinkedAgentMultiRole[];
    agents?: LinkedAgentMultiRole[];
    links?: LinkedLiterary[];

    notes?: ManifestationNotes;
    runtimeSeconds?: string;
    registered?: Schemas.ManifestationBaseDto['registered'];
    completedDate?: Schemas.ManifestationBaseDto['completedDate'];
    publishedYearNotPrintedInBook?: Schemas.ManifestationBaseDto['publishedYearNotPrintedInBook'];
    statementOfResponsibility?: Schemas.ManifestationBaseDto['statementOfResponsibility'];
    autoGenerateStatementOfResponsibility?: Schemas.ManifestationBaseDto['autoGenerateStatementOfResponsibility'];
    unnumberedPages?: Schemas.ManifestationWithExpressionDto['unnumberedPages'];
    trykkunderlagReserved?: boolean;
    updateLogs?: UpdateLog[];
  };

export type ProductOwner = Schemas.ProductOwnerDto;

export type Product = Schemas.ProductDto;
export type DDSStatus = Schemas.DDSEbookDto;

export type AgentsDescription = {
  descriptionContent?: Schemas.AgentsDescriptionDto['content'];
  descriptionLanguage?: Schemas.AgentsDescriptionDto['language'];
  descriptionAgentId?: Schemas.AgentsDescriptionDto['agentId'];
};

export type PhysicalProperties = {
  colour?: string;
  epages?: string;
  scale?: string[];
};

export type UpdateLog = Schemas.UpdateLogDto;

export type SubTitle = {
  value: string;
  parallelTitles: string[];
};

export type MainTitle = {
  value: string;
  subTitles: SubTitle[];
  parallelTitles: string[];
};

export type OtherTitle = {
  type: string;
  value: string;
};

export type ManifestationNotes = {
  generalNote?: Schemas.ManifestationBaseDto['generalNote'];
  editionNote?: Schemas.ManifestationBaseDto['editionNote'];
  editionType?: Schemas.ManifestationBaseDto['editionType'];
  attachment?: Schemas.ManifestationBaseDto['attachment'];
};

//----- FullManifestation --------------------------------------------------------------------------
/**
 * Union of work, expression and manifestation
 */
export type FullManifestation = Omit<WorkV4, 'id' | 'agents'> & {
  workId: WorkV4['id'];
  workAgents?: WorkV4['agents'];
} & Omit<ExpressionV4, 'id' | 'agents'> & {
    expressionId: ExpressionV4['id'];
    expressionAgents?: ExpressionV4['agents'];
  } & ManifestationV4;

/**
 * All manifestations loaded by ean are kept in a single cache entry, so a
 * manifestation already loaded for one set of eans is reused when requested
 * with another set of eans.
 */
export type ManifestationsCache = {
  /** Loaded manifestations by ean */
  manifestationsByEan: Record<string, FullManifestation>;
  /** Eans that have been loaded, also those without a manifestation */
  loadedEans: string[];
};

export type WorkMetadata = {
  work: WorkV4;
  expressions: ExpressionV4[];
  manifestations: ManifestationV4[];
};

export type ExpressionWithManifestations = ExpressionV4 & {
  manifestations: ManifestationV4[];
};
export type WorkWithExpressions = WorkV4 & {
  expressions?: ExpressionWithManifestations[];
};
export type Metadata = WorkMetadata & {
  changeRequests: ChangeRequest[];
};

/** Note! Assumed to be in order of strictness */
export const ManifestationStatuses: ManifestationStatus[] = [
  'NEW',
  'PRE_CATALOGED',
  'CONTROL',
  'COMPLETED',
];

export type EntityWithStatus = {
  status: ManifestationStatus;
};

export type EntityWithStatusKey<TKey extends string> = {
  [key in TKey]?: ManifestationStatus;
};

export type ExpressionIds = {
  expressionId: string;
  manifestationIds: string[];
};

export type MetadataIds = {
  workId: string;
  expressions: ExpressionIds[];
};

export type MetadataEntityType =
  | Concept.work
  | Concept.expression
  | Concept.manifestation
  | Concept.changeRequest;

export type EntityId = {
  type: MetadataEntityType;
  id: string;
};

export type EntityContainer =
  | {
      type: Concept.work;
      data: WorkV4;
    }
  | {
      type: Concept.expression;
      data: ExpressionV4;
    }
  | {
      type: Concept.manifestation;
      data: ManifestationV4;
    }
  | {
      type: Concept.changeRequest;
      data: ChangeRequest;
    };

export type SaveStatus =
  | {status: 'Saved'; error?: undefined}
  | {
      status: 'Failed';
      error: HttpError;
    };

export type OptionalSaveStatus =
  | SaveStatus
  | {status: 'None'; error?: undefined};

export type EntitySaveStatus = {
  entity: EntityId;
  status: OptionalSaveStatus;
};

export type EntityEditStatus = {
  hasDataChanged?: boolean;
  /** Only used for manifestations */
  hasMoved?: boolean;
  // TODO: Should not be pr entity, but for the whole metadata
  isSaving?: boolean;
};

export type EditStatuses = {
  [id: string]: EntityEditStatus;
};

export type MetadataTimestamps = {
  [id: string]: number;
};

export type EditMode = 'readonly' | 'edit';
export type EditModes = {
  [id: string]: {[key: string]: EditMode};
};

export type ChangeRequestItem = {
  type: Concept.changeRequest;
  taskId: string;
};

export type ManifestationItem = {
  type: Concept.manifestation;
  id: string;
  changeRequests: ChangeRequestItem[];
};

export type ExpressionGroupOrder = {
  expressionId: string;
  type: 'existing' | 'new';
  isNewExpression?: boolean;
  content: ManifestationItem[];
};

export type MetadataOrder = ExpressionGroupOrder[];

export type EntityStatus = {
  type: Concept.work | Concept.expression | Concept.manifestation;
  status: ManifestationStatus;
  previewStatus?: ManifestationStatus;
  changedStatus?: ManifestationStatus;
};

export type MetadataStatuses = {
  [id: string]: EntityStatus;
};

/**
 * Properties exported from work, to allow schema validation and
 * codelist restrictions on expressions and manifestations
 */
export type WorkExportProps = Pick<WorkV4, 'type'> &
  Partial<
    Pick<
      WorkV4,
      'agents' | 'literatureType' | 'languages' | 'intellectualLevel'
    >
  >;

export type ExpressionExportProps = Partial<
  Pick<ExpressionV4, 'expressionFormat'>
>;

export type ExpressionExports = {[id: string]: ExpressionExportProps};

export type ManifestationExportProps = Partial<
  Pick<ManifestationV4, 'productForm'>
>;

export type ManifestationExports = {[id: string]: ManifestationExportProps};

export type EntityWithId = {id: string};

/**
 * Property exports for codelist restrictions and
 * schema validations/conditions requiring access to other entities (relatedScope)
 */
export type MetadataExports = {
  work: WorkExportProps;
  expressions: ExpressionExports;
  manifestations: ManifestationExports;
};

export type MetadataChangesInfo = {
  /** Root paths of approved values */
  rootPaths: string[];
  /** Update history pr array, to be able to map original index to updated array value */
  arraysUpdateHistory: {[arrayPath: string]: ArrayUpdateHistory};
  /** Set to true when all items have been approved/rejected */
  isCompleted?: boolean;
};

export type MetadataChanges = {
  [changeRequestId: string]: MetadataChangesInfo;
};

export type MetadataEditState = {
  saving: boolean;
  // Data from backend / in sync with backend
  savedMetadata?: Metadata;
  // Data being edited
  metadata?: Metadata;
  approvedChanges: MetadataChanges;
  /** @deprecated Does not seem to be in use */
  timestamps: MetadataTimestamps;
  newExpressionIds: string[];
  order: MetadataOrder;
  statuses: MetadataStatuses;
  editStatuses: EditStatuses;
  editModes: EditModes;
  /** Property exports for codelist restrictions and schema part validation  */
  exports: MetadataExports;
};

export type ActualChangesWithSchema = Required<
  Pick<ChangeRequest, 'id' | 'taskType' | 'status'>
> & {
  workId: string;
  expresssionId: string;
  manifestationId: string;
  changes: {
    work?: Omit<WorkV4, 'type'> & Partial<Pick<WorkV4, 'type'>>; // Type is required, but not always a change
    expression?: ExpressionV4;
    manifestation?: ManifestationV4;
  };
  original: {
    work?: WorkV4;
    expression?: ExpressionV4;
    manifestation?: ManifestationV4;
  };
  externalSuggestions?: ExternalSuggestions;
  externalSuggestionsCount: number;
  count: number;
  /**
   * Paths, including entity name ('work', 'expression' or 'manifestation') to
   * each part of the schema with actual changes
   */
  paths: string[];

  /**
   * Work, expression and manifestations schemas combined and trimmed to match changes-property,
   * i.e. actual work, expression and manifestation changes
   * */
  schema: Schema;
};

export type RootKey = keyof ActualChangesWithSchema['original'];
export type DataChanges = ActualChangesWithSchema['changes'];
export type WorkImportProps = {
  work: WorkExportProps;
};

export type ExpressionImportProps = WorkImportProps & {
  expression: ExpressionExportProps;
};

export type ManifestationImportProps = ExpressionImportProps & {
  manifestation: ManifestationExportProps;
};

export type ArrayUpdate = {
  /**
   * Index which operation was performed on that version of the array (moment in history).
   * I.e. may be differnt from the index in the path to item to add or remove.
   *
   */
  atIndex: number;
  operation: ChangeRequestAction;
};

export type ArrayUpdateHistory = ArrayUpdate[];

export type SelectorById<T, TId = string> = (
  id: TId,
) => (state: RootState) => T;

export type ReviewSaveStatuses = [
  workStatus: OptionalSaveStatus,
  expressionStatus: OptionalSaveStatus,
  manifestationStatus: OptionalSaveStatus,
  taskStatus: OptionalSaveStatus,
];

export type MetadataSaveOperations = {
  saveWork: (work: WorkV4) => Promise<SaveStatus>;
  saveExpression: (expression: ExpressionV4) => Promise<SaveStatus>;
  saveManifestation: (manifestation: ManifestationV4) => Promise<SaveStatus>;
  saveMetadataEntities: (
    entities: EntityContainer[],
  ) => Promise<EntitySaveStatus[]>;
  saveNewExpressionWithManifestation: (
    expression: ExpressionV4,
    manifestation: ManifestationV4,
  ) => Promise<OptionalSaveStatus>;
  saveChangeRequest: (
    changeRequest: ChangeRequest,
    onlyChangeRequests: boolean,
  ) => Promise<SaveStatus>;
};

export type MetadataEditOperations = {
  cancelMetadataEdits: () => void;
  moveManifestationToOtherExpression: (
    manifestationId: string,
    expressionId: string,
  ) => void;
  moveManifestationToNewExpression: (manifestationId: string) => void;
  cancelMoveManifestationToNewExpression: (manifestationId: string) => void;
};

export type WorkRelationsDto = Pick<
  Schemas.WorkBaseDto,
  'workRelations' | 'agentRelations' | 'seriesRelations'
>;

export type AgentCorporationRelationsDto = Pick<
  Schemas.AgentCorporationDto,
  'agentCorporationRelations' | 'workRelations'
>;

export type AgentPersonRelationsDto = Pick<
  Schemas.AgentPersonDto,
  'agentPersonRelations' | 'workRelations'
>;

export type TypeMapping = 'generalAgent' | 'concreteAgent';

//----- DownloadFile -------------------------------------------------------------------------------
export type FileInfo = Schemas.FileInfoDto;
export type DownloadFile = Schemas.DownloadFileDto;

//----- ChangeRequest ------------------------------------------------------------------------------

// TODO: Fix type
export type ChangeRequestType =
  | Extract<
      Schemas.TaskV2Dto['type'],
      'ML_MANUAL_APPROVAL' | 'METADATA_NEW' | 'METADATA_IMPORT_CHANGE'
    >
  | 'METADATA_MOVED'
  | 'METADATA_COPIED';

/**
 * backend: change request is stored in backend.
 *  Change request from ML, publisher or other external source.
 *
 * local: change request is stored in local storage.
 *  Stored locally during moving manifestation to another work.
 *  Stored values are from the work you moved from.
 *
 * transient: change request is not stored, only in memory.
 *  Used during copying from work/manifestation to current work/manifestation.
 */
export type ChangeRequestStorage = 'backend' | 'local' | 'transient';

/**
 * work: change request is on work level.
 *  Used when copying from one work to another.
 *
 * manifestation: change request is on manifestation level.
 *  Used when moving manifestation to another work.
 *  Plus all changerequests from backend (ML, publisher, etc.).
 */
export type ChangeRequestLevel = 'work' | 'manifestation';

export type ChangeRequest = {
  /** Mapped from taskId: number to id:string in API layer */
  id: string;
  taskType: ChangeRequestType;
  storage: ChangeRequestStorage;
  level: ChangeRequestLevel;
  work: WorkV4;
  expression: ExpressionV4;
  manifestation: ManifestationV4;
  externalSuggestions?: ExternalSuggestions;
  status?: TaskStatus | undefined;
};

export type ChangeRequestData = ChangeRequest[];

export type ChangeRequestApproval = Schemas.ManualApprovalChangeRequestDto;
